// API配置 - 全站唯一的 API 地址来源，其它文件一律从这里 import，不要再写死
// 优先级：VITE_API_URL（Vercel 各环境 / 本地 .env）> vite dev 时的本地后端 > 生产后端
const PROD_API_URL = 'https://plur-production.up.railway.app/api';
export const API_BASE_URL = import.meta.env.VITE_API_URL
  || (import.meta.env.DEV ? 'http://localhost:5001/api' : PROD_API_URL);

// 当前环境标识：production / dev / local（Vercel Preview 里设 VITE_APP_ENV=dev；本地 .env 设 local）
export const APP_ENV = import.meta.env.VITE_APP_ENV || (import.meta.env.DEV ? 'local' : 'production');
// 环境一致性检查。
// 不能挂在 `APP_ENV !== 'production'` 里：APP_ENV 在非 DEV 构建且没设 VITE_APP_ENV 时
// 恰好默认成 'production'，而「两个变量都忘了配」正是最危险、最该报警的情况。
// 所以改成看当前域名：只要不是在生产域名上跑，却把请求打向生产后端，就报错。
if (typeof window !== 'undefined') {
  const host = window.location.hostname;
  const onProdHost = host === 'p1ur.com' || host === 'www.p1ur.com';
  const pointsAtProd = API_BASE_URL.includes('plur-production');

  if (!onProdHost && pointsAtProd) {
    console.error(
      `[plur] ⚠️ 环境不一致：站点跑在 ${host}，API 却指向生产后端 ${API_BASE_URL}。` +
      ' 这个部署的订单和 Stripe 扣款会写进生产 —— 检查它的 VITE_API_URL。'
    );
  } else if (!onProdHost) {
    console.info(`[plur] env=${APP_ENV} api=${API_BASE_URL}`);
  }
}

// API工具函数
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // 获取认证token
  const token = localStorage.getItem('token');
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// 认证相关API
export const authAPI = {
  // 检查邮箱是否已注册
  checkEmail: async (email) => {
    return apiRequest('/auth/check-email', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },
  
  // 用户登录
  signIn: async (email, password) => {
    return apiRequest('/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  },
  
  // 用户注册
  signUp: async (email, password, name) => {
    return apiRequest('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name })
    });
  },
  
  // 更新用户信息
  updateProfile: async (profileData) => {
    return apiRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }
};

// 产品相关API
export const productAPI = {
  // 获取所有产品
  getProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    return apiRequest(`/products${queryString ? `?${queryString}` : ''}`);
  },
  
  // 获取单个产品
  getProduct: (id) => {
    return apiRequest(`/products/${id}`);
  },
};

// 订单相关API
export const orderAPI = {
  // 创建订单
  createOrder: async (orderData) => {
    return apiRequest('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  },
  
  // 获取用户订单列表
  getMyOrders: async () => {
    return apiRequest('/orders/my-orders');
  },
  
  // 获取单个订单详情
  getOrder: async (orderNumber) => {
    return apiRequest(`/orders/${orderNumber}`);
  },
  
  // 更新订单状态
  updateOrderStatus: async (orderNumber, status) => {
    return apiRequest(`/orders/${orderNumber}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }
};

// 配置相关API
export const configAPI = {
  // 获取配置信息
  getConfig: async () => {
    return apiRequest('/config');
  }
};

// 默认导出（为了兼容旧的导入方式）
const api = {
  ...authAPI,
  ...productAPI,
  ...orderAPI
};

export default api;